import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Save, Clock, History, Download, Settings2, Trash2 } from "lucide-react";
import { PageHeader, Panel, Badge, fmtDT } from "@/components/admin/ui";

export default function StoreCount() {
  const { sid } = useParams();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState({});
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    api.get(`/inventory?store_id=${sid}`).then((r) => setItems(r.data));
    api.get(`/inventory/counts?store_id=${sid}`).then((r) => setHistory(r.data));
    api.get(`/inventory/count-status?store_id=${sid}`).then((r) => setStatus(r.data)).catch(() => {});
  }, [sid]);
  useEffect(load, [load]);

  const removeCount = async (h) => {
    if (!window.confirm("Delete this count sheet?")) return;
    try { await api.delete(`/inventory/counts/${h.id}`); toast.success("Count deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    const last = history[0];
    if (!last) return;
    const m = {};
    last.items.forEach((i) => { m[i.name] = { count: i.count, notes: i.notes || "" }; });
    setRows((prev) => (Object.keys(prev).length ? prev : m));
  }, [history]);

  const filtered = useMemo(() => items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase())), [items, q]);
  const set = (name, field, val) => setRows((r) => ({ ...r, [name]: { ...(r[name] || { count: "", notes: "" }), [field]: val } }));

  const save = async () => {
    const payload = items.map((i) => ({ item_id: i.id, name: i.name, unit: i.unit || "",
      count: parseFloat(rows[i.name]?.count) || 0, notes: rows[i.name]?.notes || "" }));
    if (!payload.length) return toast.error("No inventory items set up for this store yet.");
    setSaving(true);
    try {
      await api.post("/inventory/counts", { store_id: sid, items: payload });
      toast.success("Inventory count saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const last = history[0];
  const late = !!status?.cutoff_passed;

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Store · Inventory" title="Count Inventory" testId="count-title"
        subtitle={last ? `Last count: ${fmtDT(last.submitted_at)} by ${last.submitted_by_name}` : "No counts submitted yet."}
        actions={<>
          {isAdmin && <Link to={`/admin/stores/${sid}/inventory/manage`}><Button variant="outline" className="rounded-xl" data-testid="manage-items-link"><Settings2 className="w-4 h-4 mr-1"/>Manage Items</Button></Link>}
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[hsl(var(--primary))] h-11 px-6 text-base" data-testid="save-count">
            <Save className="w-4 h-4 mr-2"/>{saving ? "Saving…" : "Save Count"}
          </Button>
        </>}
      />

      {late && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2" data-testid="count-late-notice">
          <Clock className="w-4 h-4 shrink-0"/> All employees are required to submit their inventory count before 12:00 PM. You can still save a count now — it will be marked as late.
        </div>
      )}
      {!late && status && status.minutes_to_cutoff <= 60 && !status.store_counted_today && (
        <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2" data-testid="count-reminder">
          <Clock className="w-4 h-4 shrink-0"/> Reminder: today's count is due in {status.minutes_to_cutoff} min (12:00 PM).
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-neutral-400"/>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="pl-9 h-10 rounded-xl" data-testid="count-search"/>
      </div>

      <Panel className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="p-4">Item</th><th className="p-4 w-44">Current Quantity</th><th className="p-4">Notes</th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {filtered.length === 0 && <tr><td colSpan={3} className="p-10 text-center text-neutral-500" data-testid="count-empty">No items{q ? " match your search" : " yet"}.</td></tr>}
            {filtered.map((i) => (
              <tr key={i.id} data-testid={`count-row-${i.id}`}>
                <td className="p-3 pl-4"><div className="font-medium">{i.name}</div><div className="text-[11px] text-neutral-400">{i.unit}</div></td>
                <td className="p-3">
                  <Input type="number" inputMode="decimal" step="any" value={rows[i.name]?.count ?? ""} onChange={(e) => set(i.name, "count", e.target.value)}
                    className="h-11 text-lg font-semibold rounded-xl" placeholder="0" data-testid={`count-qty-${i.id}`}/>
                </td>
                <td className="p-3 pr-4">
                  <Input value={rows[i.name]?.notes ?? ""} onChange={(e) => set(i.name, "notes", e.target.value)} placeholder="Low, expiring, etc." className="h-11 rounded-xl" data-testid={`count-notes-${i.id}`}/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <div className="mt-10">
        <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><History className="w-4 h-4"/>Count History</h2>
        <Panel className="divide-y divide-neutral-100">
          {history.length === 0 && <p className="p-6 text-sm text-neutral-500" data-testid="history-empty">No previous counts.</p>}
          {history.map((h) => (
            <div key={h.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`history-row-${h.id}`}>
              <div>
                <div className="font-medium">{h.date} <span className="text-neutral-400 font-normal">· {h.items.length} items</span> {h.is_late && <Badge tone="orange">late</Badge>}</div>
                <div className="text-xs text-neutral-500">{fmtDT(h.submitted_at)} · {h.submitted_by_name}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setViewing(viewing?.id === h.id ? null : h)} data-testid={`history-view-${h.id}`}>{viewing?.id === h.id ? "Hide" : "View"}</Button>
                {isAdmin && <Button size="sm" variant="outline" onClick={() => removeCount(h)} className="text-red-600" data-testid={`history-delete-${h.id}`}><Trash2 className="w-3 h-3"/></Button>}
                <a href={`${api.defaults.baseURL}/inventory/counts/${h.id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="w-3 h-3"/></Button></a>
              </div>
              {viewing?.id === h.id && (
                <table className="w-full text-xs mt-2"><tbody className="divide-y divide-neutral-100">
                  {h.items.map((it, idx) => <tr key={idx}><td className="py-1.5 font-medium">{it.name}</td><td className="py-1.5 text-right">{it.count} {it.unit}</td><td className="py-1.5 pl-4 text-neutral-500">{it.notes}</td></tr>)}
                </tbody></table>
              )}
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}
